/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import configuration */
import type { OverviewAnomalyConfig } from '@/config/kpi/overviewAnomalyConfig';

/* Import libraries */
import { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import type { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';

/* Import types */
import type { TimeSeriesCalc } from '@/lib/timeSeries/timeSeriesValue';

export interface OverviewDataset {
    calc: TimeSeriesCalc[];
    conversion: number[][];
    definitionsByUrn: Map<string, OverviewAnomalyConfig>;
    fractionDigits: number[][];
    title: string[][];
    unit: Array<Array<string | null>>;
    urn: string[][];
}

export interface CreateOverviewDatasetOptions {
    enabledOnly?: boolean;
    system?: string;
}

export function createOverviewDataset(
    deviceSensorCatalog: DeviceSensorCatalog,
    anomalyConfigs: readonly OverviewAnomalyConfig[],
    {
        enabledOnly = true,
        system,
    }: CreateOverviewDatasetOptions = {},
): OverviewDataset {
    const dataset: OverviewDataset = {
        calc: [],
        conversion: [],
        definitionsByUrn: new Map(),
        fractionDigits: [],
        title: [],
        unit: [],
        urn: [],
    };

    anomalyConfigs.forEach((anomalyConfig) => {
        const deviceNames = deviceSensorCatalog
            .getVirtualDevices(anomalyConfig.group)
            .filter((deviceName) => (
                (!enabledOnly || deviceSensorCatalog.isGroupEnabled(deviceName, anomalyConfig.group)) &&
                (!system || deviceSensorCatalog.getDeviceSystem(deviceName) === system)
            ));

        deviceNames.forEach((deviceName) => {
            const deviceSensor = new DeviceSensor(deviceName, deviceSensorCatalog);
            const resolvedKpis = anomalyConfig.kpis.flatMap((kpi) => {
                const sensorGroup = kpi.sensorGroup === undefined
                    ? anomalyConfig.group
                    : kpi.sensorGroup;
                const sensorName = deviceSensor.getSensorName(sensorGroup, kpi.sensorType);

                return sensorName ? [{ kpi, sensorName }] : [];
            });

            if (resolvedKpis.length === 0) {
                return;
            }

            const urnGroup = resolvedKpis.map(({ sensorName }) => sensorName);

            urnGroup.forEach((sensorName) => {
                dataset.definitionsByUrn.set(sensorName, anomalyConfig);
            });

            dataset.calc.push(anomalyConfig.calc);
            dataset.conversion.push(resolvedKpis.map(({ kpi }) => kpi.conversion));
            dataset.fractionDigits.push(resolvedKpis.map(({ kpi }) => kpi.fractionDigits));
            dataset.title.push(resolvedKpis.map(() => anomalyConfig.label));
            dataset.unit.push(resolvedKpis.map(({ kpi }) => kpi.unit));
            dataset.urn.push(urnGroup);
        });
    });

    return dataset;
}
